// React and related hooks
import { useMemo, useState, Fragment } from 'react';
import PropTypes from 'prop-types';

// MUI components and styles
import { useTheme, alpha } from '@mui/material/styles';
import {
    Divider,
    Stack,
    Table,
    Button,
    TableCell,
    TableBody,
    TableHead,
    TableRow,
    TableContainer,
    Tooltip,
    Box,
    IconButton,
    Skeleton,
    useMediaQuery,
} from '@mui/material';
// Icons
import { Add, Archive } from 'iconsax-react';
import { toast } from 'react-toastify';
// React Table imports
import {
    flexRender,
    getCoreRowModel,
    getSortedRowModel,
    getPaginationRowModel,
    getFilteredRowModel,
    useReactTable
} from '@tanstack/react-table';

// Components
import MainCard from 'components/MainCard';
import ScrollX from 'components/ScrollX';
import AddModal from './Modal/AddModal'; // Assuming AddModal is a modal component

// Custom components and helpers
import { CSVExport, DebouncedInput, HeaderSort, RowSelection, SelectColumnSorting, TablePagination } from '../react-table';
import API from 'services/AxiosConfig';
import ConfirmArchiveModal from 'pages-new/extra-pages/Image/ConfirmArchiveModal';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
// import useCompany from 'hooks/useCompany';
// import { useAuth } from 'hooks';



function ReactTable({ data, columns, loading, handleOpen, handleEdit, buttonTitle, setData, doReorder }) {

    const theme = useTheme();
    const [sorting, setSorting] = useState([{ id: 'location', desc: false }]);
    const [columnFilters, setColumnFilters] = useState([]);
    const [rowSelection, setRowSelection] = useState({});
    const [globalFilter, setGlobalFilter] = useState('');
    const isSmallScreen = useMediaQuery((theme) => theme.breakpoints.down("sm"));

    const table = useReactTable({
        data,
        columns,
        state: {
            columnFilters,
            sorting,
            rowSelection,
            globalFilter
        },
        enableRowSelection: true,
        onSortingChange: setSorting,
        onRowSelectionChange: setRowSelection,
        onGlobalFilterChange: setGlobalFilter,
        onColumnFiltersChange: setColumnFilters,
        getRowCanExpand: () => true,
        getSortedRowModel: getSortedRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getCoreRowModel: getCoreRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        debugTable: true
    });



    const backColor = alpha(theme.palette.primary.lighter, 0.1);

    let headers = [];
    columns.forEach(
        (column) =>
            column.accessorKey &&
            headers.push({
                label: typeof column.header === 'string' ? column.header : '#',
                key: column.accessorKey
            })
    );

    return (
        <div className="table-top">
            {/* Table render */}
            <MainCard content={false}>
                <Stack
                    spacing={2}
                    direction="row"
                    justifyContent="space-between"
                    m={'0px 10px'}
                >
                    {/* Search and Sort Row */}
                    <Stack
                        direction="row"
                        gap={2}
                        spacing={2}
                        alignItems="flex-start"
                        justifyContent="space-between"
                        sx={{
                            flexWrap: isSmallScreen ? "wrap" : "nowrap", // Wrap items on small screens
                        }}
                    >
                        {/* Search Input */}
                        <DebouncedInput
                            value={globalFilter ?? ""}
                            onFilterChange={(value) => setGlobalFilter(String(value))}
                            placeholder={`Search ${data.length} records...`}
                        />
                        {/* Column Sorting */}

                    </Stack>

                    {/* Add Button Row */}
                    <Stack spacing={2} direction={isSmallScreen ? 'column' : 'row'} sx={{ marginRight: isSmallScreen ? "20px" : "0px" }} alignItems={isSmallScreen ? "flex-end" : "flex-start"}>
                        <SelectColumnSorting
                            {...{
                                getState: table.getState,
                                getAllColumns: table.getAllColumns,
                                setSorting,
                            }}
                        />
                        {!isSmallScreen && <Button
                            variant="contained"
                            startIcon={<Add />}
                            onClick={handleOpen}
                            sx={{ width: "150px" }}
                        >
                            {buttonTitle}
                        </Button>
                        }
                        {/* CSV Export Button (hidden on mobile) */}
                        {!isSmallScreen ?
                            <CSVExport
                                data={table.getRowModel().flatRows.map((row) => row.original)}
                                headers={headers}
                                filename="notes-list.csv"
                            />
                            : <></>}
                    </Stack>
                </Stack>
                <ScrollX>
                    <Stack>
                        <RowSelection selected={Object.keys(rowSelection).length} />
                        <TableContainer>
                            <Table>
                                <TableHead>
                                    {table.getHeaderGroups().map((headerGroup) => (
                                        <TableRow key={headerGroup.id}>
                                            {headerGroup.headers.map((header) => {
                                                if (header.column.columnDef.meta !== undefined && header.column.getCanSort()) {
                                                    Object.assign(header.column.columnDef.meta, {
                                                        className: header.column.columnDef.meta.className + ' cursor-pointer prevent-select'
                                                    });
                                                }
                                                return (
                                                    <TableCell
                                                        key={header.id}
                                                        {...header.column.columnDef.meta}
                                                        onClick={header.column.getToggleSortingHandler()}
                                                        {...(header.column.getCanSort() &&
                                                            header.column.columnDef.meta === undefined && {
                                                            className: 'cursor-pointer prevent-select'
                                                        })}
                                                    >
                                                        {header.isPlaceholder ? null : (
                                                            <Stack direction="row" spacing={1} alignItems="center">
                                                                <Box>{flexRender(header.column.columnDef.header, header.getContext())}</Box>
                                                                {header.column.getCanSort() && <HeaderSort column={header.column} />}
                                                            </Stack>
                                                        )}
                                                    </TableCell>
                                                );
                                            })}
                                        </TableRow>
                                    ))}
                                </TableHead>
                                <TableBody style={{ display: "contents" }}>
                                    {/* <DragDropContext
                                        onDragEnd={(result) => {
                                            if (!result.destination) return;
                                            const updatedRows = [...data];
                                            const [movedItem] = updatedRows.splice(result.source.index, 1);
                                            updatedRows.splice(result.destination.index, 0, movedItem);
                                            // Optional: Update state in parent or notify backend

                                            const reorderedWithOrder = updatedRows.map((item, index) => ({
                                                ...item,
                                                order: index + 1
                                            }));
                                            // ✅ Get pagination state from the table instance
                                            const pageIndex = table.getState().pagination.pageIndex;
                                            const pageSize = table.getState().pagination.pageSize;

                                            // ✅ Calculate the global order
                                            const globalOrder = pageIndex * pageSize + result.destination.index + 1;
                                            console.log("----globalOrder-----" + globalOrder)
                                            console.log("----result.destination.index-----" + result.destination.index)
                                            setData(reorderedWithOrder);
                                            // doReorder(movedItem.id, result.destination.index);
                                            doReorder(movedItem.id, globalOrder);
                                        }}
                                    > */}
                                    <DragDropContext
                                        onDragEnd={(result) => {
                                            if (!result.destination) return;
                                            const updatedRows = [...data];
                                            const [movedItem] = updatedRows.splice(result.source.index, 1);
                                            updatedRows.splice(result.destination.index, 0, movedItem);
                                            // Optional: Update state in parent or notify backend

                                            const reorderedWithOrder = updatedRows.map((item, index) => ({
                                                ...item,
                                                order: index + 1
                                            }));
                                            setData(reorderedWithOrder);

                                            // Get current page info
                                            const pageIndex = table.getState().pagination.pageIndex;
                                            const pageSize = table.getState().pagination.pageSize;
                                            const globalStartIndex = pageIndex * pageSize;

                                            // Get current page's rows
                                            const pageRows = table.getPaginationRowModel().rows.map(row => row.original);

                                            // Reorder only the page rows
                                            const updatedPageRows = [...pageRows];
                                            const [movedItem1] = updatedPageRows.splice(result.source.index, 1);
                                            updatedPageRows.splice(result.destination.index, 0, movedItem1);

                                            // Add global order to reordered page rows
                                            const reorderedWithOrder1 = updatedPageRows.map((item, index) => ({
                                                ...item,
                                                order: globalStartIndex + index + 1
                                            }));

                                            // Send only current page's reordered items to backend
                                            const payload = reorderedWithOrder1.map(item => ({
                                                id: item.id,
                                                order: item.order
                                            }));

                                            console.log("Sending reordered page items to backend:", payload);
                                            doReorder(payload);
                                        }}
                                    >
                                        <Droppable droppableId="table-body">
                                            {(provided) => (
                                                <tbody ref={provided.innerRef} {...provided.droppableProps}>
                                                    {(loading ? Array.from(new Array(10)) : table.getRowModel().rows).map(
                                                        (row, index) => {
                                                            // const rowData = loading ? null : row.original;
                                                            return (
                                                                <Draggable
                                                                    key={loading ? `skeleton-${index}` : row.id}
                                                                    draggableId={loading ? `skeleton-${index}` : row.id}
                                                                    index={index}
                                                                >
                                                                    {(provided, snapshot) => {
                                                                        const isDragging = snapshot.isDragging; return (
                                                                            <TableRow
                                                                                ref={provided.innerRef}
                                                                                {...provided.draggableProps}
                                                                                style={{
                                                                                    ...provided.draggableProps.style,
                                                                                    background: snapshot.isDragging ? "#f0f0f0" : "inherit",
                                                                                }}
                                                                            >
                                                                                {/* <TableCell {...provided.dragHandleProps}>⠿</TableCell> */}
                                                                                {loading ? (
                                                                                    columns.map((column) => (
                                                                                        <TableCell key={column.id || column.accessorKey}>
                                                                                            <Skeleton variant="text" width="100%" height={'30px'} />
                                                                                        </TableCell>
                                                                                    ))
                                                                                ) : (
                                                                                    row.getVisibleCells().map((cell, cellIndex) => (
                                                                                        (isDragging === true && cell.id.includes("name")) ?
                                                                                            <TableCell {...provided.dragHandleProps}
                                                                                                key={cell.id}
                                                                                                onClick={() => cellIndex === 0 ? handleEdit(row.original) : null}
                                                                                                {...cell.column.columnDef.meta}

                                                                                                style={{
                                                                                                    width:
                                                                                                        cell.id === 'action' ? '10%' : '90%', border: "none"

                                                                                                }}
                                                                                            >
                                                                                                {/* {flexRender(cell.column.columnDef.cell, cell.getContext())} */}
                                                                                                <div
                                                                                                    style={{
                                                                                                        display: "flex",
                                                                                                        alignItems: "center",       // ✅ vertical center
                                                                                                        justifyContent: "center",   // ✅ horizontal center
                                                                                                        height: "100%",             // fill parent height
                                                                                                        paddingTop: "14px",
                                                                                                        paddingLeft: "-10px", marginLeft: "-10px"
                                                                                                    }}
                                                                                                >
                                                                                                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                                                                                </div>
                                                                                            </TableCell>
                                                                                            : isDragging === false && <TableCell {...provided.dragHandleProps}
                                                                                                key={cell.id}
                                                                                                onClick={() => cellIndex === 0 ? handleEdit(row.original) : null}
                                                                                                {...cell.column.columnDef.meta}

                                                                                                style={{ width: cell.id === 'action' ? '10%' : '90%', }}
                                                                                            >
                                                                                                {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                                                                            </TableCell>
                                                                                    ))
                                                                                )}
                                                                            </TableRow>
                                                                        )
                                                                    }}
                                                                </Draggable>
                                                            );
                                                        }
                                                    )}
                                                    {provided.placeholder}
                                                </tbody>
                                            )}
                                        </Droppable>
                                    </DragDropContext>
                                </TableBody>

                                {/* <TableBody>
                                    {loading ? (
                                        renderSkeletonRows()
                                    ) : (
                                        table.getRowModel().rows.map((row, rowIndex) => (
                                            <Fragment key={row.id}>
                                                <TableRow key={row.id} style={{ cursor: 'pointer' }}>
                                                    {row.getVisibleCells().map((cell, index) => (
                                                        <TableCell
                                                            key={cell.id}
                                                            onClick={() => index === 0 ? handleEdit(row.original) : null}
                                                            {...cell.column.columnDef.meta}
                                                            style={{ cursor: 'pointer', width: index === 0 ? '90%' : '10%' }}
                                                        >
                                                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                                        </TableCell>
                                                    ))}
                                                </TableRow>
                                            </Fragment>
                                        ))
                                    )}
                                </TableBody> */}
                            </Table>
                        </TableContainer>
                        <>
                            <Divider />
                            <Box sx={{ p: 2 }} className="pagination">
                                <TablePagination
                                    setPageSize={table.setPageSize}
                                    setPageIndex={table.setPageIndex}
                                    getState={table.getState}
                                    getPageCount={table.getPageCount}
                                />
                            </Box>
                        </>
                    </Stack>
                </ScrollX>
            </MainCard>
        </div>
    );
}


ReactTable.propTypes = {
    data: PropTypes.array.isRequired,
    columns: PropTypes.array.isRequired,
    loading: PropTypes.bool.isRequired,
    handleOpen: PropTypes.func.isRequired,
    handleEdit: PropTypes.func.isRequired,
    buttonTitle: PropTypes.string,
    url: PropTypes.string,
    userId: PropTypes.string,
    setData: PropTypes.func,
    isOpen: PropTypes.bool,
    setIsOpen: PropTypes.func,
};

export default function EntryTable({ rows, loading, getData, buttonTitle, url, userId, type, setData, isOpen = false, setIsOpen }) {
    const theme = useTheme();
    const [selectedData, setSelectedData] = useState(null);
    const [isArchiveOpen, setIsArchiveOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    //const [type, setType] = useState('Active');
    const columns = useMemo(
        () => [

            // {
            //     id: 'drag',
            //     header: '',
            //     accessorKey: 'drag',
            //     cell: () => '⠿',
            //     enableSorting: false
            // },
            {
                id: 'name',
                header: 'Name',
                accessorKey: 'name'
            },
            {
                id: 'action',
                header: 'Action',
                accessorKey: 'action',
                cell: ({ row }) => (
                    <Stack direction="row" alignItems="center" spacing={0}>
                        <Tooltip title="Archive">
                            <IconButton
                                color="primary"
                                onClick={() => {
                                    handleArchive(row.original);
                                }}>
                                <Archive />
                            </IconButton>
                        </Tooltip>
                    </Stack>
                ),
            },
        ],
        [theme.palette.primary.lighter]
    );

    const handleOpen = () => {
        setSelectedData(null);
        setIsOpen(true);
    }
    const handleClose = () => {
        setIsOpen(false);
        setSelectedData(null);
    }
    const handleEdit = (data) => {
        setSelectedData(data);
        setIsOpen(true);
    };


    // const { refreshRatings, refreshCompanyRatings ,refreshactivityTypes} = useCompany();
    // const doRefresh = (url) => {
    //     if (url === 'referralRating') {
    //         refreshRatings();
    //     }
    //     else if (url === 'referralRatingCompany') {
    //         refreshCompanyRatings();
    //     }
    //     else if (url === 'referralRatingCompany') {
    //         refreshactivityTypes();
    //     }
    // };

    const checkDuplicateEntry = (name) => {
        return rows.some((entry) => entry.name.toLowerCase() === name.toLowerCase());

    }
    const handleConfirmClose = async () => {
        try {
            setIsLoading(true);
            let archived = await API.put(`${url}/activity-type/archive/${selectedData.id}`, { status: !(type === 'Active'), userId: userId })
            archived && toast.success(`Successfully ${type === 'Active' ? "Archived" : 'Unarchived'} Activity`)
            setIsArchiveOpen(false);
            setIsLoading(false);
            getData(true)
            // doRefresh(url);
        } catch (err) {
            toast.error(`Failed to ${type === 'Active' ? "Archived" : 'Unarchived'} Activity`)
        }
    }
    const doReorder = (id, idx) => {
        API.post(`user/reorder`, { name: url.replace('-', '').replace('/', '_'), items: id, order: idx })
            .then((rsp) => {
                if (rsp.data.success) {
                    toast.success('Successfully reorder.');
                    getData(true)
                }
            })
            .catch((error) => {
                toast.error('Failed to reorder.');
                console.error(error);
            });
    };
    const handleArchive = (data) => {
        setIsOpen(false);
        setSelectedData(data);
        setIsArchiveOpen(true);
    };
    const handleArchiveClose = () => {
        setSelectedData(null);
        setIsArchiveOpen(!isArchiveOpen);
    };
    return (
        <>
            <ConfirmArchiveModal isArchiveOpen={isArchiveOpen} loading={isLoading} handleArchiveClose={handleArchiveClose} handleConfirmClose={handleConfirmClose} type={type}></ConfirmArchiveModal >
            <ReactTable setData={setData} data={rows} columns={columns} handleOpen={handleOpen} handleEdit={handleEdit} handleArchive={handleArchive} loading={loading} buttonTitle={buttonTitle} doReorder={doReorder} />
            <AddModal isOpen={isOpen} onClose={handleClose} data={selectedData} getData={getData} title={buttonTitle} url={url} checkDuplicateEntry={checkDuplicateEntry} />
        </>
    );
}
